import CONFIG from "config/config";
import { callAuthorizedGraphQLEndpoint } from "../../module/graphQL";

// ─── Types ────────────────────────────────────────────────────────────────────

export type BrandKitAssetType = "logo" | "image" | "font" | "icon";

export interface BrandKitAsset {
  id: string;
  type: BrandKitAssetType;
  name: string;
  s3Url: string;
  s3Key: string;
  mimeType: string;
  fileSize: string;
  createdAt: string;
}

export interface BrandKitColor {
  id: string;
  hexValue: string;
  label: string | null;
  sortOrder: number;
}

export interface BrandKitSection {
  items: BrandKitAsset[];
  totalCount: number;
  hasMore: boolean;
}

export interface BrandKit {
  logos: BrandKitSection;
  images: BrandKitSection;
  fonts: BrandKitSection;
  icons: BrandKitSection;
  colors: BrandKitColor[];
  fontPresets: BrandKitFontPreset[];
}

// ─── Input types ──────────────────────────────────────────────────────────────

export interface SaveBrandKitAssetInput {
  type: BrandKitAssetType;
  name: string;
  s3Url: string;
  s3Key: string;
  mimeType: string;
  fileSize: string;
}

export interface SaveBrandKitColorInput {
  hexValue: string;
  label?: string;
}

export interface UpdateBrandKitColorInput {
  id: string;
  hexValue?: string;
  label?: string;
}

export interface BrandKitFontPreset {
  id: string;
  label: string;
  fontFamily: string;
  fontVariant: string;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  sortOrder: number;
}

export interface SaveBrandKitFontPresetInput {
  label?: string;
  fontFamily: string;
  fontVariant: string;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
}

export interface UpdateBrandKitFontPresetInput {
  id: string;
  label?: string;
  fontFamily?: string;
  fontVariant?: string;
  fontSize?: number;
  lineHeight?: number;
  letterSpacing?: number;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export const getBrandKit = async (limitPerType = 20) => {
  const gql = `query($limitPerType: Int) {
    getBrandKit(limitPerType: $limitPerType) {
      logos { items { id type name s3Url s3Key mimeType fileSize createdAt } totalCount hasMore }
      images { items { id type name s3Url s3Key mimeType fileSize createdAt } totalCount hasMore }
      fonts { items { id type name s3Url s3Key mimeType fileSize createdAt } totalCount hasMore }
      icons { items { id type name s3Url s3Key mimeType fileSize createdAt } totalCount hasMore }
      colors { id hexValue label sortOrder }
      fontPresets { id label fontFamily fontVariant fontSize lineHeight letterSpacing sortOrder }
    }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { limitPerType }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const getBrandKitAssets = async (type?: BrandKitAssetType, limit = 20, offset = 0) => {
  const gql = `query($type: BrandKitAssetType, $limit: Int, $offset: Int) {
    getBrandKitAssets(type: $type, limit: $limit, offset: $offset) {
      items { id type name s3Url s3Key mimeType fileSize createdAt }
      totalCount
      hasMore
    }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { type, limit, offset }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const getBrandKitColors = async () => {
  const gql = `query {
    getBrandKitColors { id hexValue label sortOrder }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, {}, CONFIG.GRAPHQL_API_BASE_URL);
};

// ─── Mutations ────────────────────────────────────────────────────────────────

export const saveBrandKitAsset = async (input: SaveBrandKitAssetInput) => {
  const gql = `mutation($input: SaveBrandKitAssetInput!) {
    saveBrandKitAsset(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const removeBrandKitAsset = async (id: string) => {
  const gql = `mutation($input: RemoveBrandKitAssetInput!) {
    removeBrandKitAsset(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input: { id } }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const saveBrandKitColor = async (input: SaveBrandKitColorInput) => {
  const gql = `mutation($input: SaveBrandKitColorInput!) {
    saveBrandKitColor(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const updateBrandKitColor = async (input: UpdateBrandKitColorInput) => {
  const gql = `mutation($input: UpdateBrandKitColorInput!) {
    updateBrandKitColor(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const removeBrandKitColor = async (id: string) => {
  const gql = `mutation($input: RemoveBrandKitColorInput!) {
    removeBrandKitColor(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input: { id } }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const reorderBrandKitColors = async (orderedIds: string[]) => {
  const gql = `mutation($input: ReorderBrandKitColorsInput!) {
    reorderBrandKitColors(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input: { colorIds: orderedIds } }, CONFIG.GRAPHQL_API_BASE_URL);
};

// ─── Font Preset Mutations ───────────────────────────────────────────────────

export const saveBrandKitFontPreset = async (input: SaveBrandKitFontPresetInput) => {
  const gql = `mutation($input: SaveBrandKitFontPresetInput!) {
    saveBrandKitFontPreset(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const updateBrandKitFontPreset = async (input: UpdateBrandKitFontPresetInput) => {
  const gql = `mutation($input: UpdateBrandKitFontPresetInput!) {
    updateBrandKitFontPreset(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const removeBrandKitFontPreset = async (id: string) => {
  const gql = `mutation($input: RemoveBrandKitFontPresetInput!) {
    removeBrandKitFontPreset(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input: { id } }, CONFIG.GRAPHQL_API_BASE_URL);
};

export const reorderBrandKitFontPresets = async (orderedIds: string[]) => {
  const gql = `mutation($input: ReorderBrandKitFontPresetsInput!) {
    reorderBrandKitFontPresets(input: $input) { success error }
  }`;
  return callAuthorizedGraphQLEndpoint(gql, { input: { presetIds: orderedIds } }, CONFIG.GRAPHQL_API_BASE_URL);
};
